import React, { useEffect, useMemo, useState } from 'react'
import ContentHeader from './ContentHeader'
import {
  useHormonalAutoimmune,
  useI18n,
  useInsuranceCardEntry,
  usePersonalInfo,
  useRehabSelectedThemes,
  useSurvey,
  useTalkAboutCoverage,
  useUnavailableStatus,
} from 'contexts/AuthContext'
import { Button } from 'antd'
import _ from 'lodash'
import notEligibleIcon from './img/ic-not-eligible.svg'
import { useUpdateClient } from 'hooks/careHooks'
import { getHealthieUserId, getLocalLanguage } from 'utils/authLocalStorage'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { mcaScreenerPromise, updateUserPromise } from 'apis/FitOnCareAPI'
import { logBrazeEvent } from 'utils/brazeInit'
import ButtonMailTo from 'components/ButtonMailTo/index'
import { canCloseToFitOnWeb, onCareFlowFinished } from './Rehab/util'
import { CareReason } from './Rehab/returnTo'
import { getInsuranceProviderForAmplitude } from './util'
import { getMbiFailedInfo } from './MCA/mbiFailedCache'
import { isMobile } from 'utils/basic'

export const UnavailableStatus = Object.freeze({
  COMING_SOON_STATE: 1,
  LIMITED_AVAILABILITY_SURVEY: 2,
  LIMITED_AVAILABILITY_HORMONAL_AUTOIMMUNE: 3,
  LIMITED_AVAILABILITY_SCHEDULED_TIME: 4,
  NOT_ELIGIBLE_TALK_ABOUT_COVERAGE: 5,
  NOT_ELIGIBLE_ADDRESS: 6,
  NOT_ELIGIBLE_CONGRATULATIONS: 7,
  NOT_ELIGIBLE_STATE: 8,
  AUTO_LOGIN_STATE_UNAVAILABLE: 9,
})

const Unavailable = (props) => {
  const { i18n } = useI18n()
  const { sku, from, userStatus, savedData } = props
  const { rehabSelectedThemes } = useRehabSelectedThemes()
  const { updateClient } = useUpdateClient()
  const { talkAboutCoverage } = useTalkAboutCoverage()
  const { survey } = useSurvey()
  const { hormonalAutoimmune } = useHormonalAutoimmune()
  const {
    personalInfo: { state },
  } = usePersonalInfo()
  const { insuranceCardEntry } = useInsuranceCardEntry()
  const { unavailableStatus } = useUnavailableStatus()

  const [isLoading, setIsLoading] = useState(false)

  const { theme } = useMemo(() => {
    return {
      theme: rehabSelectedThemes?.map((theme) => theme.name).join(','),
    }
  }, [rehabSelectedThemes])

  useEffect(() => {
    props.setRemoveEventListener(true)
  }, [])

  const getDetail = () => {
    switch (sku) {
      case 'msk':
      case 'pt':
        if (savedData.noPTs) {
          return 'No PTs'
        } else {
          return getInsuranceProviderForAmplitude(talkAboutCoverage)
        }
      case 'mca':
        return getMbiFailedInfo().unavailableReason
      default:
        if (savedData.noRDs) {
          return 'No RDs'
        } else {
          if (survey.name === 'Other') {
            return `${survey.name} - ${survey.otherText}`
          } else if (hormonalAutoimmune.name) {
            if (hormonalAutoimmune.name === 'Other') {
              return `${survey.name} - ${hormonalAutoimmune.name} - ${hormonalAutoimmune.otherText}`
            } else {
              return `${survey.name} - ${hormonalAutoimmune.name}`
            }
          } else {
            return survey.name
          }
        }
    }
  }

  const getReason = () => {
    switch (sku) {
      case 'msk':
      case 'pt':
        if (savedData.noPTs) {
          return 'Schedule'
        } else {
          return 'Condition'
        }
      case 'mca':
        return getMbiFailedInfo().unavailableReason
      default:
        if (savedData.noRDs) {
          return 'Schedule'
        } else {
          return 'Condition'
        }
    }
  }

  useEffect(() => {
    switch (unavailableStatus) {
      case UnavailableStatus.COMING_SOON_STATE:
        updateUserPromise({
          activate: 1,
          skuStatus: {
            sku,
            status: 4,
          },
        })

        if (!_.isEmpty(state)) {
          logAmplitudeEvent('Screen View: Onboarding - Not Available', {
            Reason: 'State',
            Detail: state.code,
            State: state.name,
            sku: sku,
            language: getLocalLanguage(),
          })
          logBrazeEvent(`User Denied: State - ${sku}`)
        }
        break

      case UnavailableStatus.LIMITED_AVAILABILITY_SURVEY:
      case UnavailableStatus.LIMITED_AVAILABILITY_SCHEDULED_TIME:
      case UnavailableStatus.LIMITED_AVAILABILITY_HORMONAL_AUTOIMMUNE:
        logAmplitudeEvent('Screen View: Onboarding - Not Available', {
          Reason: getReason(),
          Detail: getDetail(),
          State: state?.name,
          sku: sku,
          language: getLocalLanguage(),
        })
        switch (sku) {
          case 'msk':
          case 'pt':
            logBrazeEvent('User Denied: Insurance - pt')
            break
          default:
            break
        }
        break

      case UnavailableStatus.NOT_ELIGIBLE_TALK_ABOUT_COVERAGE:
      case UnavailableStatus.NOT_ELIGIBLE_CONGRATULATIONS:
      case UnavailableStatus.NOT_ELIGIBLE_ADDRESS:
      case UnavailableStatus.NOT_ELIGIBLE_STATE:
        if (state.available) {
          logBrazeEvent(`User Denied: Insurance - ${sku}`)
        } else {
          logBrazeEvent(`User Denied: State - ${sku}`)
        }

        if (insuranceCardEntry.coverage === 'HMO') {
          updateUserPromise({
            skuStatus: {
              sku,
              status: 7,
            },
          })
          logAmplitudeEvent('Screen View: Onboarding - Not Available', {
            Reason: 'Insurance Type',
            Detail: `HMO`,
            State: state?.name,
            sku: sku,
            theme: theme,
            language: getLocalLanguage(),
          })
        } else {
          updateUserPromise({
            skuStatus: {
              sku,
              status: state.available ? 6 : 4,
            },
          })
          if (state?.available) {
            logAmplitudeEvent('Screen View: Onboarding - Not Available', {
              Reason: 'Insurance Provider',
              Detail: `${talkAboutCoverage.name}${
                talkAboutCoverage.name === 'Other'
                  ? ' - ' + talkAboutCoverage.otherText
                  : ''
              }${userStatus === 7 ? ' - No PCP' : ''}`,
              State: state?.name,
              sku: sku,
              theme: theme,
              language: getLocalLanguage(),
            })
          } else {
            logAmplitudeEvent('Screen View: Onboarding - Not Available', {
              Reason: 'State',
              Detail: state?.name,
              sku: sku,
              theme: theme,
              language: getLocalLanguage(),
            })
          }
        }
        break

      default:
        if (sku === 'mca') {
          logAmplitudeEvent('Screen View: Onboarding - Not Available', {
            sku,
            Reason: getReason(),
            Detail: getDetail(),
          })
        }
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    insuranceCardEntry.coverage,
    userStatus,
    sku,
    state,
    talkAboutCoverage.name,
    talkAboutCoverage.otherText,
    theme,
    unavailableStatus,
  ])

  const onNext = () => {
    setIsLoading(true)
    updateClient({
      variables: {
        id: getHealthieUserId(),
        active: false,
      },
      onSuccess: () => {
        switch (unavailableStatus) {
          case UnavailableStatus.NOT_ELIGIBLE_TALK_ABOUT_COVERAGE:
            onCareFlowFinished({
              sku,
              success: false,
              programId: 0,
              from,
              reason: CareReason.notCovered,
            })
            setIsLoading(false)
            break
          default:
            if (sku === 'mca') {
              mcaScreenerPromise().then((resp) => {
                const fitonUserId = resp.data?.fitonUserId?.toString()
                onCareFlowFinished({
                  sku,
                  success: false,
                  programId: 0,
                  from,
                  fitonUserId,
                })
                setIsLoading(false)
              })
            } else if (canCloseToFitOnWeb({ sku, from })) {
              // CARE-160: a rehab member with an allowed returnTo goes back to FitOn web with the fail.
              onCareFlowFinished({ sku, success: false, programId: 0, from })
              setIsLoading(false)
            } else {
              window.location.href = 'https://www.fitoncare.com'
            }
            break
        }
      },
    })
  }

  const onBack = (e) => {
    updateUserPromise({ activate: 0, skuStatus: { sku, status: 1 } })
    props.setRemoveEventListener(false)
    props.onBack(e)
  }

  return (
    <div className='info-sec'>
      <ContentHeader {...props} onBack={onBack} />
      <div className='info-content'>
        <img src={notEligibleIcon} alt='' className='top-icon' />
        <div className='title'>{i18n('Currently unavailable')}</div>
        <div
          className='subtitle'
          style={{ marginBottom: '10px', marginTop: '17px' }}
        >
          {i18n(
            "Unfortunately, based on your selection, we are unable to enroll you in the program. Don't worry -- we'll notify you as soon as that changes."
          )}
          <br />
          <br />
          {i18n(
            'For any questions, please reach out anytime to: {0}',
            <ButtonMailTo
              label='care@fitoncare.com'
              email='care@fitoncare.com'
              className='gradient-font-1'
            />
          )}
        </div>
        <Button
          className='btn-primary small-btn'
          onClick={onNext}
          loading={isLoading}
        >
          {isMobile() && sku === 'mca' ? i18n('Back to App') : i18n('ok')}
        </Button>
      </div>
    </div>
  )
}

export default Unavailable
